/**
 * Interview Prep (completion plan P5).
 *
 * The catalog says which interview formats can be practised now and which
 * preparation sources are available. A session is started through the
 * ordinary coaching API (`createCoachingSession` with `interviewPrep`), on the
 * canonical conversation runtime — there is no separate interview session API.
 *
 * Preparing from a source (paste / upload / link) is stateless: the server
 * returns a normalised context for the learner to review and edit, and keeps
 * nothing. Saving a profile is an explicit, separate action (and only while
 * the server says profiles are enabled).
 */
import { api } from '../api';

export type InterviewType = 'behavioural' | 'situational' | 'role_specific';

export interface InterviewTypeEntry {
  interviewType: InterviewType;
  label: string;
  description: string;
  /** content_incomplete: no interview scenario or interviewer persona has been authored yet. */
  status: 'available' | 'content_incomplete';
  gap?: 'no_authored_scenario' | 'interviewer_persona_unavailable';
  scenario?: { id: string; title: string; description: string; difficulty: string; estimatedMinutes: number };
  interviewer?: { personaKey: string; label: string };
  /** Testing needs a rubric the scenario's author chose. */
  testingAvailable: boolean;
  developmentRubric: { id: string; version: string };
}

export interface PrepLimits {
  roleTitleMaxChars: number;
  seniorityMaxChars: number;
  itemMaxChars: number;
  listMaxItems: number;
  sourceRefMaxChars: number;
}

export interface InterviewPrepCatalog {
  types: InterviewTypeEntry[];
  limits: {
    targetRoleMaxChars: number;
    jobDescriptionMaxChars: number;
    drillTopicMaxChars?: number;
    sourceMaxChars?: number;
    uploadMaxBytes?: number;
    uploadExtensions?: string[];
    prep?: PrepLimits;
    profileLabelMaxChars?: number;
    maxProfiles?: number;
  };
  /** Which preparation sources this environment offers. Absent on an older server: paste only. */
  sources?: { paste: boolean; upload: boolean; url: boolean; marketplace: boolean };
  /** Whether saving a prep profile is available here. */
  profilesEnabled?: boolean;
}

export type PrepSource = 'paste' | 'upload' | 'url' | 'marketplace';
export type PrepFieldOrigin = 'MODEL_INFERRED' | 'LEARNER_EDITED';

/** BeHeard's normalised preparation context. Mirrors the server's domain/interviewPrep/prepContext.ts. */
export interface PrepContext {
  provenance: { source: PrepSource; sourceRef: string | null; fetchedAt: string; contentHash: string };
  roleTitle: { value: string; origin: PrepFieldOrigin } | null;
  seniority: { value: string; origin: PrepFieldOrigin } | null;
  responsibilities: { items: string[]; origin: PrepFieldOrigin };
  requirements: { items: string[]; origin: PrepFieldOrigin };
  skills: { items: string[]; origin: PrepFieldOrigin };
  interviewTopics: { items: string[]; origin: PrepFieldOrigin };
  extraction: 'model' | 'unavailable';
}

/** What a "prepare from a source" call answers. */
export interface PreparedSource {
  context: PrepContext;
  /** The text that was read, for the learner to check. Not stored anywhere. */
  sourceText: string;
  guard: { droppedItems: number };
}

export interface PrepProfile {
  id: string;
  label: string;
  context: PrepContext;
  createdAt: string;
  updatedAt: string;
}

export interface PrepAttempt {
  sessionId: string;
  status: string;
  mode: 'training' | 'testing';
  interviewType: string | null;
  practiceKind: 'interview' | 'question_drill';
  startedAt: string;
  endedAt: string | null;
  /** Null when the attempt was not measured (never shown as 0). */
  overall: number | null;
}

export interface PrepProgressSummary {
  attempts: number;
  completed: number;
  measured: number;
  averageOverall: number | null;
  change: number | null;
}

/** What the learner sends when starting. Their own words; the server validates and caps them. */
export interface InterviewPrepRequest {
  interviewType: InterviewType;
  targetRole?: string;
  jobDescription?: string;
  prep?: PrepContext;
  drillTopic?: string;
  prepProfileId?: string;
}

export interface PrepProfileHistory {
  profileId: string;
  attempts: PrepAttempt[];
  summary: PrepProgressSummary;
}

interface Envelope<T> {
  success: boolean;
  data: T;
}

export const interviewPrepApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getInterviewPrepCatalog: builder.query<InterviewPrepCatalog, void>({
      query: () => '/coaching/interview-prep',
      transformResponse: (response: Envelope<InterviewPrepCatalog>) => response.data,
      providesTags: ['Scenarios'],
    }),
    // Prepare from a source: stateless on the server, so nothing to cache or invalidate.
    prepareFromPaste: builder.mutation<PreparedSource, { text: string }>({
      query: (body) => ({ url: '/coaching/interview-prep/prepare/paste', method: 'POST', body }),
      transformResponse: (response: Envelope<PreparedSource>) => response.data,
    }),
    prepareFromUpload: builder.mutation<PreparedSource, FormData>({
      query: (body) => ({ url: '/coaching/interview-prep/prepare/upload', method: 'POST', body }),
      transformResponse: (response: Envelope<PreparedSource>) => response.data,
    }),
    prepareFromUrl: builder.mutation<PreparedSource, { url: string }>({
      query: (body) => ({ url: '/coaching/interview-prep/prepare/url', method: 'POST', body }),
      transformResponse: (response: Envelope<PreparedSource>) => response.data,
    }),
  }),
  overrideExisting: false,
});

/** Saved prep profiles (explicit opt-in) and their attempt history. Own tag, registered here. */
export const interviewPrepProfilesApi = api.enhanceEndpoints({ addTagTypes: ['InterviewPrepProfiles'] }).injectEndpoints({
  endpoints: (builder) => ({
    listPrepProfiles: builder.query<PrepProfile[], void>({
      query: () => '/coaching/interview-prep/profiles',
      transformResponse: (response: Envelope<{ profiles: PrepProfile[] }>) => response.data.profiles,
      providesTags: ['InterviewPrepProfiles'],
    }),
    savePrepProfile: builder.mutation<PrepProfile, { label: string; context: PrepContext }>({
      query: (body) => ({ url: '/coaching/interview-prep/profiles', method: 'POST', body }),
      transformResponse: (response: Envelope<PrepProfile>) => response.data,
      invalidatesTags: ['InterviewPrepProfiles'],
    }),
    updatePrepProfile: builder.mutation<PrepProfile, { id: string; label?: string; context?: PrepContext }>({
      query: ({ id, ...body }) => ({ url: `/coaching/interview-prep/profiles/${encodeURIComponent(id)}`, method: 'PATCH', body }),
      transformResponse: (response: Envelope<PrepProfile>) => response.data,
      invalidatesTags: ['InterviewPrepProfiles'],
    }),
    deletePrepProfile: builder.mutation<void, string>({
      query: (id) => ({ url: `/coaching/interview-prep/profiles/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: ['InterviewPrepProfiles'],
    }),
    getPrepProfileAttempts: builder.query<PrepProfileHistory, string>({
      query: (id) => `/coaching/interview-prep/profiles/${encodeURIComponent(id)}/attempts`,
      transformResponse: (response: Envelope<PrepProfileHistory>) => response.data,
      providesTags: ['InterviewPrepProfiles'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetInterviewPrepCatalogQuery,
  usePrepareFromPasteMutation,
  usePrepareFromUploadMutation,
  usePrepareFromUrlMutation,
} = interviewPrepApi;

export const {
  useListPrepProfilesQuery,
  useSavePrepProfileMutation,
  useUpdatePrepProfileMutation,
  useDeletePrepProfileMutation,
  useGetPrepProfileAttemptsQuery,
} = interviewPrepProfilesApi;
